/**
 * Tiny site-icon component.
 *
 * Prefers the server-provided `favicon_url` (Google s2 service, cached on
 * the Website / Competitor row). Falls back to a coloured letter avatar
 * derived from the label so the UI never renders an empty box while the
 * Celery detector is still working.
 *
 * Used in: sidebar website picker, Websites list, Competitors list,
 * dashboard header.
 */

import { useState } from "react";

function deriveHost(url = "") {
  try {
    const u = new URL(url.includes("://") ? url : `https://${url}`);
    return u.hostname.replace(/^www\./, "");
  } catch {
    return (url || "").replace(/^https?:\/\//, "").split("/")[0];
  }
}

function colourFromString(seed) {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 45%, 55%)`;
}

export default function SiteFavicon({
  url,
  domain,
  label = "",
  size = 20,
  className = "",
  rounded = "rounded-md",
}) {
  const [errored, setErrored] = useState(false);
  const host = deriveHost(domain || "");
  const fallbackUrl = host
    ? `https://www.google.com/s2/favicons?domain=${host}&sz=${Math.max(32, size * 2)}`
    : "";
  const src = url || fallbackUrl;

  const dimension = { width: size, height: size };
  const letter = (label || host || "?").trim().charAt(0).toUpperCase();

  if (!src || errored) {
    return (
      <span
        aria-hidden="true"
        className={`inline-flex items-center justify-center text-white text-[10px] font-semibold flex-shrink-0 ${rounded} ${className}`}
        style={{ ...dimension, background: colourFromString(host || label || letter) }}
      >
        {letter}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      onError={() => setErrored(true)}
      className={`inline-block bg-white object-contain flex-shrink-0 ${rounded} ring-1 ring-slate-200 ${className}`}
      style={dimension}
    />
  );
}
